// frontend/src/utils/snippetExtractor.js
// 前端片段提取工具 - 從完整內容中搜尋關鍵字並提取多個片段

/**
 * 從完整內容中提取包含關鍵字的多個片段
 * @param {string} content - 完整文件內容
 * @param {string|string[]} keywords - 關鍵字（可以是字串或陣列）
 * @param {Object} options - 選項
 * @param {number} options.contextLength - 前後文長度（預設 50 字）
 * @param {number} options.maxSnippets - 最多提取幾個片段（預設 10）
 * @returns {Array} 片段陣列
 */
export function extractSnippetsFromContent(content, keywords, options = {}) {
  const { contextLength = 50, maxSnippets = 10 } = options;

  // 1. 處理關鍵字
  const keywordArray = Array.isArray(keywords) ? keywords : [keywords];

  // 2. 找出所有匹配位置
  const matches = [];

  keywordArray.forEach((keyword) => {
    if (!keyword || keyword.trim().length === 0) return;

    const cleanKeyword = keyword.trim();
    let index = 0;

    while (index < content.length) {
      const found = content.indexOf(cleanKeyword, index);
      if (found === -1) break;

      matches.push({
        keyword: cleanKeyword,
        position: found,
        length: cleanKeyword.length,
      });

      index = found + cleanKeyword.length;
    }
  });

  // 3. 按位置排序
  matches.sort((a, b) => a.position - b.position);

  // 4. 去除重疊的匹配（保留最早的）
  const uniqueMatches = [];
  let lastEnd = -1;

  matches.forEach((match) => {
    if (match.position > lastEnd) {
      uniqueMatches.push(match);
      lastEnd = match.position + match.length;
    }
  });

  // 5. 限制數量
  const limitedMatches = uniqueMatches.slice(0, maxSnippets);

  // 6. 提取片段（包含前後文）
  const snippets = limitedMatches.map((match, index) => {
    const start = Math.max(0, match.position - contextLength);
    const end = Math.min(content.length, match.position + match.length + contextLength);

    let snippet = content.substring(start, end);

    // 添加省略號
    if (start > 0) snippet = '...' + snippet;
    if (end < content.length) snippet = snippet + '...';

    // 高亮關鍵字
    const highlightedSnippet = snippet.replace(
      new RegExp(escapeRegex(match.keyword), 'g'),
      `<mark>${match.keyword}</mark>`
    );

    return {
      index: index + 1,
      snippet: highlightedSnippet,
      position: match.position,
      keyword: match.keyword,
    };
  });

  return snippets;
}

/**
 * 轉義正則表達式特殊字元
 * @param {string} str - 字串
 * @returns {string} 轉義後的字串
 */
function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 從搜尋查詢中提取關鍵字（處理 AND/OR/NEAR/NOT 邏輯）
 * @param {string} query - 搜尋查詢（可能包含 +, /, NEAR, NOT 等運算子）
 * @returns {string[]} 關鍵字陣列
 */
export function extractKeywordsFromQuery(query) {
  if (!query || query.trim().length === 0) return [];

  let processedQuery = query;

  // 1. 處理 NEAR 語法：將 "詞1 NEAR[n] 詞2" 或 "詞1 NEAR 詞2" 轉換為 "詞1 + 詞2"
  // 這樣後續可以統一用 + 分割來提取關鍵字
  processedQuery = processedQuery.replace(/(\S+)\s+NEAR(?:\[\d+\])?\s+(\S+)/gi, '$1 + $2');

  // 2. 處理 NOT 語法：移除 NOT 後面的詞（因為我們只需要高亮要搜尋的詞，不是排除的詞）
  // "詞1 NOT 詞2" -> "詞1"
  processedQuery = processedQuery.replace(/\s+NOT\s+\S+/gi, '');

  // 3. 處理 - 排除語法：移除 -詞 的部分
  // "詞1 -詞2" -> "詞1"
  processedQuery = processedQuery.replace(/\s+-\S+/g, '');

  // 4. 移除 + 和 / 符號，按空白分割提取關鍵字
  const keywords = processedQuery
    .split(/[+/]/)
    .map((k) => k.trim())
    .filter((k) => k.length > 0);

  return keywords;
}

/**
 * 將搜尋結果按文件 ID 分組，並為每個文件提取多個片段
 * @param {Array} results - 原始搜尋結果（扁平化列表）
 * @param {string} query - 搜尋查詢
 * @param {Object} options - 選項
 * @returns {Array} 分組後的結果
 */
export function groupResultsWithMultipleSnippets(results, query, options = {}) {
  const grouped = {};

  results.forEach((result) => {
    const docId = result.id;

    if (!grouped[docId]) {
      // 第一次遇到這個文件，建立新的分組
      grouped[docId] = {
        documentId: docId,
        title: result.title,
        source: result.source,
        sourceName: result.sourceName, // 來源中文名稱（從 sources 表 JOIN 取得）
        origin: result.origin,
        created_at: result.created_at,
        tags: result.tags,
        snippets: [],
        totalMatches: result.totalMatches || 0, // [2025-12-19] 實際出現總數
        access_level: result.access_level, // 特殊資料 chip 依賴此欄位（restricted/public）
      };

      // 優先順序：
      // 1. 使用後端已處理好的 snippets 陣列（效能最佳）
      // 2. 使用單一 snippet 字串（最後手段）
      // 註：不再從 result.content 前端自行提取片段（U4）——後端搜尋路徑一律不回 content，
      // 且前端提取的 position 是原文空間，與後端 matchOffset 的 NFKC 空間不同源

      if (result.snippets && Array.isArray(result.snippets) && result.snippets.length > 0) {
        // 後端已經提供高亮片段，直接使用
        grouped[docId].snippets = result.snippets.map((s, idx) => ({
          index: idx + 1,
          snippet: s.snippet || s.text || s,
          // [Plan B C1 / 2026-07-30] position 取後端 matchOffset（命中位置，NFKC 空間）。
          // 後端 snippet 的 `offset` 是窗口起點（match.start - contextLength），**不可**
          // 拿來當 fallback —— 會系統性偏移一個 contextLength，密集命中時吸到錯的一筆。
          // 缺 matchOffset（如舊回應）時回 null，讓下游退回「第一個命中」的行為。
          // `??` 不可寫成 `||`：matchOffset 為 0（命中在文首）是合法值。
          position: s.matchOffset ?? null,
          keyword: s.keyword || '',
        }));
      } else {
        // 最後手段：使用單一 snippet 字串（無命中位置可言 → position 為 null）
        grouped[docId].snippets = [
          {
            index: 1,
            snippet: result.snippet || '(無摘要)',
            position: null,
          },
        ];
      }
    }
    // 如果已經處理過這個文件，就跳過（因為我們只需要一次完整內容）
  });

  return Object.values(grouped);
}
